'use client';
import { useState } from 'react';

const HomePricing = () => {
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  return (
    <section className='py-[120px] px-6 max-w-[1100px] mx-auto text-center' id='pricing'>
      <div className='text-xs font-bold uppercase tracking-[0.12em] text-primary mb-3.5'>PRICING</div>
      <h2
        className='font-display font-black leading-[1.05] tracking-[-0.04em] mb-4.5'
        style={{ fontSize: 'clamp(32px, 4.5vw, 52px)' }}
      >
        <span className='gradient-text'>Pricing</span>
      </h2>
      <p className='text-[17px] text-[hsl(var(--text-secondary))] max-w-[560px] mx-auto leading-[1.7]'>
        Start free. Upgrade anytime.
      </p>
      <p className='text-[15px] text-[hsl(var(--text-secondary))] max-w-[500px] mx-auto leading-[1.7] mt-3'>
        Most scheduling tools make pricing complicated. Tindlo keeps it simple — <strong>start free</strong>, and only
        upgrade when your team <strong>actually needs more</strong>.
      </p>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-5 max-w-[820px] mx-auto mt-12'>
        {/* Free */}
        <div
          className='relative bg-card border rounded-(--radius) p-11 flex flex-col text-left transition-all hover:-translate-y-1.5 hover:shadow-[0_20px_60px_rgba(0,0,0,.05)]'
          style={{
            background: 'linear-gradient(160deg, rgba(0,230,118,.04), rgba(0,200,83,.08))',
            borderColor: '#00c853',
            boxShadow: '0 8px 40px rgba(0,200,83,.06)',
          }}
        >
          <div
            className='absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold uppercase tracking-[0.05em] px-4.5 py-1 rounded-full text-white'
            style={{ background: 'linear-gradient(135deg, #00c853, #00e676)' }}
          >
            Most Popular
          </div>
          <div className='font-display text-2xl font-black'>Free</div>
          <div className='text-sm text-[hsl(var(--text-secondary))] mb-7'>Perfect for getting started</div>
          <div className='font-display text-[52px] font-black tracking-[-0.04em] mb-7'>
            $0<span className='text-base font-medium text-muted-foreground'>/month</span>
          </div>
          <ul className='flex flex-col gap-3.5 mb-9 flex-1'>
            {[
              'Limited Calendar History: 50 days',
              'Limited Storage: 500MB',
              'Limited Dayview: 100 Tasks',
              'Limited Roadmap: 10 Blocks & 10 Milestones',
              'Limited Kanban & Branch: 50 Days',
              'Limited Snapspace: 50 Bricks',
              'Basic file upload: 1 file per task',
            ].map(item => (
              <li
                key={item}
                className='flex items-start gap-2.5 text-sm text-[hsl(var(--text-secondary))] leading-normal'
              >
                <span
                  className='w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5'
                  style={{ background: 'rgba(0,200,83,.1)', color: '#00c853' }}
                >
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          <a
            href='https://app.tindlo.com/signin'
            target='_blank'
            onMouseEnter={() => setHoveredBtn('free')}
            onMouseLeave={() => setHoveredBtn(null)}
            className='relative w-full py-4 rounded-full text-[15px] font-bold text-center text-white transition-all duration-300 overflow-hidden'
            style={{
              background:
                hoveredBtn === 'free'
                  ? 'linear-gradient(135deg, hsl(263,84%,55%), hsl(271,91%,65%), hsl(20,100%,55%))'
                  : 'linear-gradient(135deg, #00e676, #00c853)',
              boxShadow:
                hoveredBtn === 'free'
                  ? '0 0 20px 4px rgba(124,58,237,.5), 0 0 60px 8px rgba(168,85,247,.3), 0 0 100px 16px rgba(255,100,30,.15)'
                  : '0 4px 15px rgba(0,200,83,.15)',
              transform: hoveredBtn === 'free' ? 'scale(1.05)' : 'scale(1)',
              animation: hoveredBtn === 'free' ? 'btnFire 0.6s ease-in-out infinite' : 'none',
            }}
          >
            {hoveredBtn === 'free' && (
              <span
                className='absolute inset-0 rounded-full pointer-events-none'
                style={{
                  background:
                    'radial-gradient(ellipse at 50% 100%, rgba(255,120,0,.35) 0%, rgba(168,85,247,.2) 40%, transparent 70%)',
                  animation: 'firePulse 0.5s ease-in-out infinite alternate',
                }}
              />
            )}
            <span className='relative z-10'>Get Started Free</span>
          </a>
        </div>

        {/* Standard */}
        <div
          className='bg-card border rounded-(--radius) p-11 flex flex-col text-left transition-all hover:-translate-y-1.5 hover:shadow-[0_20px_60px_rgba(0,0,0,.05)]'
          style={{
            background: 'linear-gradient(160deg, rgba(124,58,237,.04), rgba(168,85,247,.08))',
            borderColor: 'rgba(124,58,237,.2)',
          }}
        >
          <div className='font-display text-2xl font-black'>Standard</div>
          <div className='text-sm text-[hsl(var(--text-secondary))] mb-7'>For power users and teams</div>
          <div className='font-display text-[52px] font-black tracking-[-0.04em] mb-7'>
            $7<span className='text-base font-medium text-muted-foreground'>/month</span>
          </div>
          <ul className='flex flex-col gap-3.5 mb-9 flex-1'>
            {[
              'Unlimited Calendar',
              '10GB Storage',
              'Unlimited Tasks / Blocks / Milestones',
              'Unlimited New Kanban',
              'Unlimited Branch',
              'Unlimited Snapspace',
              'Team Billing Support',
            ].map(item => (
              <li
                key={item}
                className='flex items-start gap-2.5 text-sm text-[hsl(var(--text-secondary))] leading-normal'
              >
                <span
                  className='w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5'
                  style={{ background: 'rgba(0,200,83,.1)', color: '#00c853' }}
                >
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          <a
            href='https://app.tindlo.com/signin'
            target='_blank'
            onMouseEnter={() => setHoveredBtn('standard')}
            onMouseLeave={() => setHoveredBtn(null)}
            className='relative w-full py-4 rounded-full text-[15px] font-bold text-center text-background transition-all duration-300 overflow-hidden'
            style={{
              background:
                hoveredBtn === 'standard'
                  ? 'linear-gradient(135deg, hsl(263,84%,55%), hsl(271,91%,65%), hsl(20,100%,55%))'
                  : 'hsl(var(--foreground))',
              boxShadow:
                hoveredBtn === 'standard'
                  ? '0 0 20px 4px rgba(124,58,237,.5), 0 0 60px 8px rgba(168,85,247,.3), 0 0 100px 16px rgba(255,100,30,.15)'
                  : '0 4px 15px rgba(0,0,0,.1)',
              transform: hoveredBtn === 'standard' ? 'scale(1.05)' : 'scale(1)',
              animation: hoveredBtn === 'standard' ? 'btnFire 0.6s ease-in-out infinite' : 'none',
            }}
          >
            {hoveredBtn === 'standard' && (
              <span
                className='absolute inset-0 rounded-full pointer-events-none'
                style={{
                  background:
                    'radial-gradient(ellipse at 50% 100%, rgba(255,120,0,.35) 0%, rgba(168,85,247,.2) 40%, transparent 70%)',
                  animation: 'firePulse 0.5s ease-in-out infinite alternate',
                }}
              />
            )}
            <span className=' text-white relative z-10'>Subscribe Now</span>
          </a>
        </div>
      </div>

      <style>{`
        @keyframes btnHeartbeat {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(0,200,83,.2); }
          14% { transform: scale(1.06); box-shadow: 0 6px 25px rgba(0,200,83,.4); }
          28% { transform: scale(1); box-shadow: 0 4px 15px rgba(0,200,83,.2); }
          42% { transform: scale(1.04); box-shadow: 0 6px 20px rgba(0,200,83,.35); }
          56% { transform: scale(1); box-shadow: 0 4px 15px rgba(0,200,83,.2); }
        }
        @keyframes btnFire {
          0%, 100% { transform: scale(1.05); filter: brightness(1.1); }
          50% { transform: scale(1.08); filter: brightness(1.3); }
        }
        @keyframes firePulse {
          0% { opacity: 0.5; transform: scaleY(0.9); }
          100% { opacity: 0.9; transform: scaleY(1.1); }
        }
      `}</style>

      <p className='mt-7 text-sm text-muted-foreground'>
        Need help choosing? —{' '}
        <a href='mailto:hello@tindlo.com' className='text-primary font-semibold hover:underline'>
          hello@tindlo.com
        </a>
      </p>
    </section>
  );
};

export default HomePricing;
